import React, { useState, useEffect } from "react";
import axios, { AxiosRequestHeaders } from "axios";
import { Link } from "react-router-dom";
import loaderimage from "../../images/giphy.gif";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";


const EmployersUserList = () => {
  const [userList, setUserList] = useState<any[]>([]);
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [actionType, setActionType] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEmployers, setTotalEmployers] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState("");


  const headers: AxiosRequestHeaders = {
    "x-access-token": localStorage.getItem("jwttoken") || "",
  };


  const callApiUsersList = async (page: number = 1): Promise<void> => {
    try {
      setLoader(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_URL}users/employers?page=${page}&limit=${pageSize}`;
      const response = await axios.get(url, { headers });
      setUserList(response.data.employers || response.data);
      setFilteredUsers(response.data.employers || response.data);
      setCurrentPage(response.data.currentPage || page);
      setTotalPages(response.data.totalPages || 1);
      setTotalEmployers(response.data.totalEmployers || (response.data.length || 0));
    } catch (error: any) {
      setError(error.message || "Failed to fetch employer users.");
      setFailureMessage(error.message || "Failed to fetch employer users.");
      console.error("Error fetching employer users:", error);
    } finally {
      setLoader(false);
    }
  };


  useEffect(() => {
    callApiUsersList();
  }, []);


  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
  };


  const handleAction = async () => {
    if (!selectedUserId) return;


    try {
      let url = "";
      if (actionType === "Delete") {
        url = `${process.env.REACT_APP_API_URL}users/removeuser/${selectedUserId}`;
        await axios.delete(url, { headers });
        setSuccessMessage("User deleted successfully.");
      } else if (actionType === "Activate") {
        url = `${process.env.REACT_APP_API_URL}users/activateuser/${selectedUserId}`;
        await axios.put(url,{}, { headers });
        setSuccessMessage("User activated successfully.");
      } else if (actionType === "Deactivate") {
        url = `${process.env.REACT_APP_API_URL}users/deactivateuser/${selectedUserId}`;
        await axios.put(url,{}, { headers });
        setSuccessMessage("User deactivated successfully.");
      }


      callApiUsersList();
      setShowSuccessModal(true);
    } catch (error: any) {
      setFailureMessage(`Failed to ${actionType} user. ${error.message || ""}`);
      setShowFailureModal(true);
    } finally {
      setShowModal(false);
      setSelectedUserId(null);
    }
  };


  const triggerActionModal = (action: string, userId: number) => {
    setSelectedUserId(userId);
    setActionType(action);
    if (action === "Delete") {
      setModalMessage("Are you sure you want to delete this user?");
    } else if (action === "Activate") {
      setModalMessage("Are you sure you want to activate this user?");
    } else if (action === "Deactivate") {
      setModalMessage("Are you sure you want to deactivate this user?");
    }
    setShowModal(true);
  };


  const handleSendEmail = async (userId: number) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}users/welcomeemployer/${userId}`;
      await axios.get(url, { headers });
      setSuccessMessage("Welcome email sent successfully.");
      setShowSuccessModal(true);
    } catch (error: any) {
      setFailureMessage(`Failed to send email. ${error.message || ""}`);
      setShowFailureModal(true);
    }
  };


  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (searchTerm === "") {
        setFilteredUsers(userList);
      } else {
        const filtered = userList.filter(
          (user) =>
            user?.username?.toLowerCase().includes(searchTerm) ||
            user?.name?.toLowerCase().includes(searchTerm) ||
            user?.email?.toLowerCase().includes(searchTerm)
        );
        setFilteredUsers(filtered);
      }
    }, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, userList]);
  // CHANGE: Page change handler remains the same as CandidateList
  const handlePageChange = (page: number) => {
    callApiUsersList(page);
  };

  // CHANGE: Calculate the entry range using totalEmployers
  const startEntry = (currentPage - 1) * pageSize + 1;
  const endEntry = Math.min(currentPage * pageSize, totalEmployers);

  return (
    <>
      <div className="">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-black fw-bold">Employer User Management</h2>
          <div className="d-flex gap-2 align-items-center w-50" style={{ width: "510px" }}>
            <span className="fw-bold text-secondary text-nowrap">
              {filteredUsers.length} results
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by Name or Username or Email"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link to="/useradd" className="btn bg-info">
              + Add User
            </Link>
          </div>
        </div>
        <div className="card shadow-sm border-0 rounded">
          <div className="card-header bg-info border-bottom py-3">
            <h5 className="mb-0 text-white">Employer User List</h5>
          </div>
          <div className="card-body p-0">
            <div
              className="table-responsive sticky-table-container"
            >
              <table className="table table-hover align-middle mb-0">
                <thead
                  className="table-light position-sticky top-0"
                >
                  <tr>
                    <th>#</th>
                    <th>User ID</th>
                    <th>Company ID</th>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Password</th>
                    <th>Status</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr>
                      <td colSpan={9} className="text-center py-2">
                        <img src={loaderimage} alt="Loading..." height="40" />
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan={9} className="text-center text-danger fw-bold py-3">
                        <div className="alert alert-danger text-center m-3" role="alert">
                          Oops! Something went wrong. Please try again later.
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers.length > 0 ? (
                    filteredUsers.map((item, index) => (
                      <tr key={item.userid} className="border-bottom">
                        <td>{index + 1}</td>
                        <td>{item.userid}</td>
                        <td>{item.company_id}</td>
                        <td>{item.username}</td>
                        <td>{item.name}</td>
                        <td>{item.email}</td>
                        <td>{item.mobile}</td>
                        <td>{item.password}</td>
                        <td className="text-center">
                          <span
                            className={`me-2 rounded-circle d-inline-block ${item.isactive === 1 ? "bg-success" : "bg-danger"
                              }`}
                            style={{
                              width: "20px",
                              height: "20px",
                            }}
                          ></span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className={`btn btn-sm w-100 ${item.isactive === 1
                                  ? "btn-outline-danger"
                                  : "btn-outline-success"
                                }`}
                              onClick={() =>
                                triggerActionModal(
                                  item.isactive === 1 ? "Deactivate" : "Activate",
                                  item.userid
                                )
                              }
                            >
                              {item.isactive === 1 ? "Deactivate" : "Activate"}
                            </button>
                            <Link
                              to={`/useredit/${item.userid}`}
                              className="btn btn-sm btn-outline-warning"
                            >
                              Edit
                            </Link>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => triggerActionModal("Delete", item.userid)}
                            >
                              Delete
                            </button>
                            <button
                              className={`btn btn-sm ${item.isactive != 4 ? 'btn-secondary' : 'btn-outline-info'}`}
                              disabled={item.isactive != 4}
                              onClick={() => handleSendEmail(item.userid)}
                            >
                              Email
                            </button>


                            <Link
                              to={`/companiesview/${item.company_id}/${item.userid}`}
                              className="btn btn-sm btn-outline-primary"
                            >
                              Company Details
                            </Link>



                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center text-muted fw-bold">
                        <div className="alert alert-secondary text-center m-3" role="alert">
                          No Users Available
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="position-sticky bottom-0 bg-white py-2 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <div className="text-muted ps-3">
                  Showing {startEntry}-{endEntry} of {totalEmployers} entries
                </div>
                <nav>
                  <ul className="pagination mb-0 pe-3">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                    </li>
                    {[...Array(totalPages)].map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${currentPage === index + 1 ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(index + 1)}
                        >
                          {index + 1}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showModal}
        onHide={() => setShowModal(false)}
        modalMessage={modalMessage}
        confirmation={handleAction}
        operationType={actionType}
      />
      <NewconfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        modalMessage={successMessage}
        operationType="OK"
        timeoutDuration={3000}
      />
      <AllFailureModal
        show={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        modalMessage={failureMessage}
        operationType="OK"
        timeoutDuration={5000}
      />
    </>
  );
};


export default EmployersUserList;


